import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Modal,
  Select,
  Skeleton,
  Text,
  TextInput,
} from "@mantine/core";
import { Search } from "tabler-icons-react";
import flat from "flat";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import { loadingStates, validateEmail } from "../../utilities/utilities";
import AgreementListModal from "./AgreementListModal";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getAgreementCategoriesActive } from "../../utilities/apis/agreementCategories";
import { DEFAULT_AGREEMENT_TYPE_OPTIONS } from "../../components/AgreementTypeFilter";

const AgreementForm1 = ({ form, setUiConfigs, uiConfigs, save }) => {
  const agreementFormJson = flat.unflatten(form.values);
  const saveNext = async () => {
    // 1. Initialize a clean local error object
    const keys = {};

    // 2. Destructure for cleaner logic
    const relType = agreementFormJson?.relationshipType;
    const relWith = agreementFormJson?.relationshipWith;
    const category = agreementFormJson?.typeOfAgreement;
    const reqUser = agreementFormJson?.requestingUser;

    // 3. Document Type Validation
    if (!relType || relType.trim().length < 3) {
      keys.relationshipType = "type of document is mandatory.";
    }

    // 4. Connected Agreement
    const isSupplementary =
      relType === "Supplementary" ||
      relType === "Supplementary to Supplementary";
    if (isSupplementary) {
      // Check if it's null, undefined, or empty string
      if (!relWith || relWith.toString().trim().length === 0) {
        keys.relationshipWith = "connected agreement is mandatory.";
      }
    }

    // 5. Category & Title Validation
    // if (!category || !category.trim()) {
    //   keys.typeOfAgreement = "type is mandatory.";
    // }

    // 6. User Details Validation
    if (!reqUser?.name || reqUser.name.trim().length < 3) {
      keys["requestingUser.name"] = "please enter 3 or more characters.";
    }

    if (!reqUser?.email || !validateEmail(reqUser.email)) {
      keys["requestingUser.email"] = "please enter correct email.";
    }

    // 7. Execution Logic

    if (Object.keys(keys).length > 0) {
      // Using your component's specific error setter
      form.setErrors(keys);

      showNotification({
        color: "red",
        title: "agreement form",
        message: "please check all fields are filled properly.",
      });
    } else {
      // Clear errors if any existed from a previous failed attempt
      form.setErrors({});

      const response = await save();
      if (response) {
        setUiConfigs({
          ...uiConfigs,
          currentStep: 1,
        });
      } else {
        showNotification({
          color: "red",
          title: "agreement form",
          message: "something went wrong.",
        });
      }
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(0);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const handleSearchClick = () => {
    // Set the showModal state to true when the button is clicked
    setShowModal(true);
  };

  const handleCloseModal = () => {
    // Set the showModal state to false when the modal is closed
    setShowModal(false);
  };
  const typeOfDocuments = [
    "Parent",
    "Supplementary",
    "Supplementary to Supplementary",
  ];

  useEffect(() => {
    const loadAgreementTypes = async () => {
      setLoading(true);
      try {
        const resp = await apiWrapWithErrorWithData(
          getAgreementCategoriesActive(),
        );
        if (Array.isArray(resp?.data)) {
          setOptions(
            resp.data.map((item) => ({
              label: item.name,
              value: item.name,
            })),
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadAgreementTypes();
  }, []);

  useEffect(() => {
    if (selectedAgreement) {
      form.setFieldValue("relationshipWith", `${selectedAgreement}`);
    }
  }, [selectedAgreement]);

  return (
    <div className="w-full flex flex-col my-4">
      <div className="grid grid-cols-2">
        <div>
          <Select
            required
            className="max-w-lg"
            label="Type of document"
            placeholder="select type of document"
            data={typeOfDocuments.map((value) => ({ value, label: value }))}
            style={{ maxWidth: "82%" }}
            {...form.getInputProps("relationshipType")}
            onCreate={(query) => {
              form.setFieldValue(
                "relationshipType",
                Array.from(
                  new Set([...(form.values.relationshipType || []), query]),
                ),
              );
            }}
          />
        </div>
        <div style={{ maxWidth: "82%" }} className="flex">
          <TextInput
            label="Connected agreement"
            placeholder="Enter agreement number"
            {...form.getInputProps("relationshipWith")}
            required={
              agreementFormJson.relationshipType === "Supplementary" ||
              agreementFormJson.relationshipType ===
                "Supplementary to Supplementary"
            }
            type="number"
            onChange={(event) => {
              // const value = event.currentTarget.value;
              // const commaSeparatedValues = value.split(",").map((v) => v.trim());
              form.setFieldValue("relationshipWith", event.currentTarget.value);
            }}
            style={{ width: "88%", display: "inline-block", marginRight: "2%" }}
          />
          <Button
            className="max-w-lg"
            color="#46BDE1"
            onClick={handleSearchClick} // Call the handler on button click
            style={{
              width: "10%",
              display: "inline-block",
              marginBottom: "0",
              marginTop: "auto",
              padding: "0",
              backgroundColor: "#46BDE1",
            }}
          >
            <Search size={20} strokeWidth={3} />
          </Button>
        </div>
      </div>
      {/* <div className="grid grid-cols-2">
        <div></div>
        <Button
          className="mt-2 max-w-lg"
          color="green"
          onClick={handleSearchClick} // Call the handler on button click
          style={{maxWidth:"42%"}}
        >
          Search for Agreements
        </Button>
      </div> */}
      {agreementFormJson.typeOfAgreementData.length === 0 ||
      agreementFormJson.typeOfAgreementData.length ? (
        <div className="grid grid-cols-2 mt-4">
          <Select
            searchable
            required
            creatable
            className="max-w-lg"
            style={{ maxWidth: "82%" }}
            label="Agreement category"
            placeholder="select agreement category"
            data={DEFAULT_AGREEMENT_TYPE_OPTIONS}
            onCreate={(query) => {
              form.setFieldValue(
                "typeOfAgreementData",
                Array.from(
                  new Set([...(form.values.typeOfAgreementData || []), query]),
                ),
              );
            }}
            {...form.getInputProps("typeOfAgreement")}
          />
          <TextInput
            label="Job order"
            style={{ maxWidth: "82%" }}
            placeholder="enter job order"
            {...form.getInputProps("jobOrder")}
          />
        </div>
      ) : (
        <Skeleton height={50} />
      )}

      {showModal && (
        <Modal
          overflow="inside"
          opened
          onClose={handleCloseModal} // Call the handler on modal close
          size="calc(80vw)"
          title="Select Agreement"
        >
          <AgreementListModal
            onCloseAgreement={handleCloseModal}
            setSelectedAgreement={setSelectedAgreement}
          />
        </Modal>
      )}

      <Divider className="mt-8" />
      <Text size="md" color="#46BDE1" className="my-8">
        Requesting user details
      </Text>
      <div className="grid grid-cols-2 gap-x-10 gap-y-4">
        <TextInput
          required
          label="Name"
          placeholder="enter name"
          {...form.getInputProps("requestingUser.name")}
        />
        <TextInput
          required
          label="Email"
          placeholder="enter email"
          {...form.getInputProps("requestingUser.email")}
        />
        {/* <TextInput
          label="Phone"
          placeholder="Enter 10 digit phone no."
          {...form.getInputProps("requestingUser.phone")}
        /> */}
        <TextInput
          label="Designation"
          placeholder="enter designation"
          {...form.getInputProps("requestingUser.designation")}
        />
        <TextInput
          label="Department"
          placeholder="enter department"
          {...form.getInputProps("requestingUser.businessDepartment")}
        />
      </div>
      <div className="flex justify-end">
        <Button
          className="max-w-xs mt-20"
          style={{
            backgroundColor: "#46BDE1",
          }}
          onClick={saveNext}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          {uiConfigs.loading === loadingStates.LOADING ? (
            <BeatLoader color={"#fff"} size={10} />
          ) : (
            "Save & Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default AgreementForm1;
